import { useId, type SVGProps } from 'react';

type LogoProps = SVGProps<SVGSVGElement>;

interface Props {
  readonly className?: string;
  readonly descriptor?: string;
  readonly testId?: string;
}

function NadoutongLogo(props: LogoProps): JSX.Element {
  const id = useId();
  return (
    <svg viewBox="0 0 160 160" fill="none" aria-hidden="true" {...props}>
      <defs>
        <linearGradient
          id={`flow-${id}`}
          x1="18"
          y1="110"
          x2="118"
          y2="20"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#2563EB" />
          <stop offset="0.52" stopColor="#06B6D4" />
          <stop offset="1" stopColor="#7C3AED" />
        </linearGradient>
        <linearGradient
          id={`soft-${id}`}
          x1="40"
          y1="74"
          x2="112"
          y2="44"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#2563EB" stopOpacity="0.74" />
          <stop offset="1" stopColor="#7C3AED" stopOpacity="0.82" />
        </linearGradient>
      </defs>
      <g transform="translate(24 24) scale(0.62)">
        <path
          d="M72 55H126C148 55 166 73 166 95V111"
          stroke={`url(#flow-${id})`}
          strokeWidth="13.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M78 91H125C140 91 152 103 152 118V132"
          stroke={`url(#soft-${id})`}
          strokeWidth="10.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.9"
        />
        <path
          d="M78 129H120"
          stroke={`url(#soft-${id})`}
          strokeWidth="10.5"
          strokeLinecap="round"
          opacity="0.86"
        />
        <path
          d="M35 170C58 154 79 148 105 150H139C165 150 184 138 197 116"
          stroke={`url(#flow-${id})`}
          strokeWidth="14"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M35 170C58 154 79 148 105 150H139C165 150 184 138 197 116"
          stroke="white"
          strokeOpacity="0.48"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <circle cx="72" cy="55" r="8" fill="#FFFFFF" stroke="#06B6D4" strokeWidth="3.2" />
        <circle cx="166" cy="95" r="7.2" fill="#FFFFFF" stroke="#7C3AED" strokeWidth="3.2" />
        <circle cx="152" cy="132" r="6.5" fill="#FFFFFF" stroke="#2563EB" strokeWidth="3" />
        <circle cx="35" cy="170" r="8.5" fill="#FFFFFF" stroke="#2563EB" strokeWidth="3.3" />
        <circle cx="197" cy="116" r="8.5" fill="#FFFFFF" stroke="#06B6D4" strokeWidth="3.3" />
      </g>
    </svg>
  );
}

export function BrandWordmark({ className, descriptor, testId }: Props): JSX.Element {
  return (
    <div
      className={['flex items-center gap-3', className ?? ''].join(' ').trim()}
      data-testid={testId}
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-surface-container-low">
        <NadoutongLogo className="h-9 w-9 shrink-0" />
      </div>
      <div className="flex min-w-0 flex-col">
        <span className="block truncate text-[24px] font-black tracking-[-0.04em] text-on-surface">
          哪都通
        </span>
        {descriptor !== undefined && descriptor.length > 0 ? (
          <span className="block truncate text-[11px] font-semibold uppercase tracking-[0.12em] text-primary">
            {descriptor}
          </span>
        ) : null}
      </div>
    </div>
  );
}
